import { ActionReducerMapBuilder, nanoid } from '@reduxjs/toolkit';
import { ChatRole } from 'modules/chat/domain/enums/chat-role';
import { ChatErrorCode, ChatRequestError, isChatForbiddenError, readChatRequestError } from 'modules/chat/domain/interfaces';
import { clearChatHistory, loadChatHistory, sendChatMessage } from './actions';
import { ChatState } from './chat-store';

type ReducerBuilder = ActionReducerMapBuilder<ChatState>;

/** Текст сервера кладётся в ленту как есть, код — пустым `content` с `errorCode`: переводит `ui/`. */
function appendAssistantError(state: ChatState, error: ChatRequestError) {
  state.messages.push({
    id: nanoid(),
    role: ChatRole.ASSISTANT,
    content: error.message ?? '',
    errorCode: error.message ? undefined : error.code,
    createdAt: Date.now(),
  });
}

function applyChatFailure(state: ChatState, payload: unknown, fallback: ChatErrorCode, appendForbidden: boolean) {
  const chatError = readChatRequestError(payload, fallback);

  if (isChatForbiddenError(chatError)) {
    if (appendForbidden) {
      appendAssistantError(state, chatError);
    }
    return;
  }

  state.error = chatError;
}

export function createSendChatMessageReducer(builder: ReducerBuilder) {
  builder.addCase(sendChatMessage.pending, (state, action) => {
    state.messages.push({
      id: nanoid(),
      role: ChatRole.USER,
      content: action.meta.arg,
      createdAt: Date.now(),
    });
    state.isSending = true;
    state.error = undefined;
  });

  builder.addCase(sendChatMessage.fulfilled, (state, action) => {
    state.messages = state.messages.map((message) =>
      message.pendingConfirmation ? { ...message, pendingConfirmation: null } : message,
    );
    state.messages.push({
      id: nanoid(),
      role: ChatRole.ASSISTANT,
      content: action.payload.answer,
      createdAt: Date.now(),
      pendingConfirmation: action.payload.pendingConfirmation,
    });
    state.quota = action.payload.quota ?? undefined;
    state.pendingConfirmation = action.payload.pendingConfirmation;
    state.isSending = false;
  });

  builder.addCase(sendChatMessage.rejected, (state, action) => {
    state.isSending = false;
    applyChatFailure(state, action.payload, 'SEND_FAILED', true);
  });
}

export function createLoadChatHistoryReducer(builder: ReducerBuilder) {
  builder.addCase(loadChatHistory.pending, (state) => {
    state.isLoadingHistory = true;
    state.error = undefined;
  });

  builder.addCase(loadChatHistory.fulfilled, (state, action) => {
    if (!state.isSending) {
      state.messages = action.payload;
    }
    state.isLoadingHistory = false;
  });

  builder.addCase(loadChatHistory.rejected, (state, action) => {
    state.isLoadingHistory = false;
    applyChatFailure(state, action.payload, 'LOAD_FAILED', !state.isSending);
  });
}

export function createClearChatHistoryReducer(builder: ReducerBuilder) {
  builder.addCase(clearChatHistory.pending, (state) => {
    state.isClearing = true;
    state.error = undefined;
  });

  builder.addCase(clearChatHistory.fulfilled, (state) => {
    state.messages = [];
    state.pendingConfirmation = null;
    state.isClearing = false;
  });

  builder.addCase(clearChatHistory.rejected, (state, action) => {
    state.isClearing = false;
    applyChatFailure(state, action.payload, 'CLEAR_FAILED', true);
  });
}
